import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User } from 'lucide-react';
import { Card } from '../common/Card';
import { EvaluationForm } from './EvaluationForm';
import { EvaluationSummaryCard } from './EvaluationSummaryCard';
import { EvaluationBreakdown } from './EvaluationBreakdown';
import { api } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import type {
  Evaluation,
  PublicFramework,
  PredefinedRedFlag,
  SaveEvaluationRequest,
  ProjectStatus,
} from '../../types';

interface EvaluationsSectionProps {
  projectId: string;
  projectStatus: ProjectStatus;
  framework: PublicFramework;
  redFlagDefinitions: PredefinedRedFlag[];
  aiEvaluation?: Evaluation;
  humanEvaluation?: Evaluation;
  otherReviewerEvaluations: Evaluation[];
  isOwnProject: boolean;
}

export function EvaluationsSection({
  projectId,
  projectStatus,
  framework,
  redFlagDefinitions,
  aiEvaluation,
  humanEvaluation,
  otherReviewerEvaluations,
  isOwnProject,
}: EvaluationsSectionProps) {
  const [isEditing, setIsEditing] = useState(!humanEvaluation);
  const [expandedEvaluationId, setExpandedEvaluationId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const submitMutation = useMutation({
    mutationFn: (data: SaveEvaluationRequest) =>
      api.submitEvaluation(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      showToast('Evaluation submitted successfully', 'success');
      setIsEditing(false);
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 403) {
        showToast('You do not have permission to submit evaluations', 'error');
      } else if (error.status === 400 || error.status === 422) {
        // Surface the server's validation message (400: framework drift, 422: non-draft status)
        showToast(error.message, 'error');
      } else {
        showToast('Failed to submit evaluation. Please try again.', 'error');
      }
    },
  });

  const toggleBreakdown = (evaluationId: string) => {
    setExpandedEvaluationId((prev) => (prev === evaluationId ? null : evaluationId));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setExpandedEvaluationId(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // Conflict of interest check
  if (isOwnProject) {
    return (
      <Card className="bg-amber-500/10 border-amber-500/30">
        <div className="flex items-center gap-3 text-amber-200">
          <User className="w-5 h-5" />
          <span>You cannot evaluate your own project.</span>
        </div>
      </Card>
    );
  }

  // Check project status. Once consensus is reached, evaluations stay
  // visible but are read-only (no form, no edit buttons, no locks).
  const canEvaluate =
    projectStatus === 'AI_EVALUATED' || projectStatus === 'UNDER_REVIEW';
  const isReadOnly = projectStatus === 'CONSENSUS_REACHED';

  if (!canEvaluate && !isReadOnly) {
    const isPendingAI = projectStatus === 'PENDING_EVALUATION' && !aiEvaluation;
    return (
      <Card className="bg-wolf-800/30">
        <div className="text-wolf-400 text-sm">
          {isPendingAI
            ? 'Waiting for AI evaluation. An admin can trigger the evaluation using the button in the project header.'
            : 'This project is not ready for evaluation.'}
        </div>
      </Card>
    );
  }

  // Edit/Form mode - show evaluation form
  if (isEditing && canEvaluate) {
    return (
      <div>
        {/* AI Score Summary - shows overall score only, no breakdown */}
        {aiEvaluation && (
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-action font-mono text-sm">AI</span>
                  <span className="text-wolf-300 font-medium">Score</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-2xl font-bold text-action">
                    {aiEvaluation.total_score.toFixed(1)}
                  </span>
                  <span className="text-wolf-500 text-sm">/100</span>
                </div>
                {aiEvaluation.red_flags.length > 0 && (
                  <span className="text-red-400 text-sm">
                    {aiEvaluation.red_flags.length} red flag(s)
                  </span>
                )}
              </div>
            </div>
            <p className="text-wolf-500 text-xs mt-3">
              Full breakdown visible after you submit your evaluation
            </p>
          </Card>
        )}

        <div className="flex items-center gap-3 mb-6">
          <div className="w-3 h-px bg-action" />
          <span className="font-mono text-xs uppercase tracking-widest text-action">
            Your Evaluation
          </span>
        </div>

        <EvaluationForm
          framework={framework}
          redFlagDefinitions={redFlagDefinitions}
          existingEvaluation={humanEvaluation}
          isSubmitting={submitMutation.isPending}
          onSubmit={(data) => submitMutation.mutate(data)}
          onCancel={humanEvaluation ? handleCancelEdit : undefined}
        />
      </div>
    );
  }

  // Cards view - show all evaluation summary cards
  const hasHumanEvaluation = !!humanEvaluation;

  // Get the evaluation to show breakdown for
  const expandedEvaluation = expandedEvaluationId
    ? expandedEvaluationId === 'human'
      ? humanEvaluation
      : expandedEvaluationId === 'ai'
      ? aiEvaluation
      : otherReviewerEvaluations.find((e) => e.evaluator_id === expandedEvaluationId)
    : null;

  // Get label for expanded evaluation
  const expandedLabel = expandedEvaluationId === 'human'
    ? 'YOUR'
    : expandedEvaluationId === 'ai'
    ? 'AI'
    : expandedEvaluation?.evaluator_name || '';

  return (
    <div className="space-y-6">
      {/* Summary cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* YOUR evaluation card */}
        {humanEvaluation && (
          <EvaluationSummaryCard
            evaluationId="human"
            label="YOUR"
            score={humanEvaluation.total_score}
            decision={humanEvaluation.decision}
            redFlagCount={humanEvaluation.red_flags.length}
            isExpanded={expandedEvaluationId === 'human'}
            showEditButton={!isReadOnly}
            onToggleDetails={toggleBreakdown}
            onEdit={handleEdit}
          />
        )}

        {/* AI evaluation card */}
        {aiEvaluation && (
          <EvaluationSummaryCard
            evaluationId="ai"
            label="AI"
            score={aiEvaluation.total_score}
            decision={aiEvaluation.decision}
            redFlagCount={aiEvaluation.red_flags.length}
            isExpanded={expandedEvaluationId === 'ai'}
            onToggleDetails={toggleBreakdown}
          />
        )}

        {/* Other reviewers' cards */}
        {otherReviewerEvaluations.map((evaluation) => (
          <EvaluationSummaryCard
            key={evaluation.evaluator_id}
            evaluationId={evaluation.evaluator_id}
            label={evaluation.evaluator_name}
            score={evaluation.total_score}
            decision={evaluation.decision}
            redFlagCount={evaluation.red_flags.length}
            isExpanded={expandedEvaluationId === evaluation.evaluator_id}
            isLocked={!hasHumanEvaluation && !isReadOnly}
            onToggleDetails={toggleBreakdown}
          />
        ))}
      </div>

      {/* Expanded breakdown (conditionally rendered) */}
      {expandedEvaluation && (
        <EvaluationBreakdown
          evaluation={expandedEvaluation}
          label={expandedLabel}
          framework={framework}
          redFlagDefinitions={redFlagDefinitions}
          showEditButton={expandedEvaluationId === 'human' && !isReadOnly}
          onEdit={handleEdit}
          onClose={() => setExpandedEvaluationId(null)}
        />
      )}
    </div>
  );
}
